import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Divider,
  Menu,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  ArrowBack as ArrowBackIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Image as ImageIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Menu as MenuIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ViewColumn as ViewColumnIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';
import FloorPlanModal from '../components/detail/unit/FloorPlanModal';
import UnitActionsMenu, { type UnitMenuAnchor } from '../components/detail/unit/UnitActionsMenu';
import {
  logPublicFloorPlanComparisonOpened,
  logPublicFloorPlanOpened,
  logPublicProjectResearchTabViewed,
  logPublicTowerViewOpened,
} from '../services/analytics';
import { useFloorPlanCompare } from '../hooks/useFloorPlanCompare';
import {
  buildPublicUnitSyntheticKey,
  type PublicUnitForMenu,
} from '../components/detail/unit/types';
import {
  revealSection,
  type RevealedTowerViewUnitPrices,
} from '../services/superPickerPublicApi';
import { useRevealedPrices } from '../services/revealedPrices';
import { getPublicShareUuid } from '../services/shareContext';
import { useTokenSpend } from '../components/TokenSpendDialog';
import TowerViewSection from '../components/detail/TowerViewSection';
import SitePlanSectionDetail from '../components/detail/SitePlanSectionDetail';
import { useMobileOptimizedStyles } from '../hooks/mobile-hooks';
import type {
  PublicTowerViewUnit,
  PublicSitePlanPayload,
  PublicProjectDetails,
} from '../services/superPickerPublicApi';
import {
  fetchPublicTowerViewUnits,
  fetchPublicSitePlan,
  fetchPublicStackSoldPercentages,
  fetchPublicProjectDetails,
} from '../services/superPickerPublicApi';
import type { SitePlanBlock, SitePlanBounds, StackUnitInfo } from '../types';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import {
  fetchPublicDistricts,
  fetchPublicProjectCategories,
  searchPublicProjects,
  searchPublicUnits,
  type PublicDistrictOption,
  type PublicProjectCategoryOption,
  type PublicResearchFilters,
  type PublicResearchProject,
  type PublicResearchUnit,
} from '../services/superPickerPublicApi';
import {
  formatOnemapLocationLabel,
  haversineDistanceKm,
  parseOnemapCoordinates,
  searchPublicOnemapLocations,
  type PublicOneMapSearchResult,
} from '../utils/onemapPublicSearch';

/** Format a digit string for display with thousands separators (agent parity). */
const formatThousandsInput = (value: string): string => {
  if (!value) return '';
  const cleaned = value.replace(/,/g, '');
  const [intPart, decPart] = cleaned.split('.');
  if (!intPart) return decPart !== undefined ? `.${decPart}` : '';
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
};

/**
 * Public-side "Project Research" page. Front-end + mock data only — no backend
 * wiring yet. Mirrors the agent's master-tool tab in
 * `super-picker/SuperPickerApp.tsx` (Search Criteria + Project / Unit views).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ResearchFilters {
  priceMin: string;
  priceMax: string;
  psfMin: string;
  psfMax: string;
  sizeMin: string;
  sizeMax: string;
  bedroomMin: string;
  bedroomMax: string;
  floorMin: string;
  floorMax: string;
  bathMin: string;
  bathMax: string;
  districts: string[];
  regions: string[];
  tenures: string[];
  categories: string[];
  topYearMin: string;
  topYearMax: string;
}

const EMPTY_FILTERS: ResearchFilters = {
  priceMin: '1', priceMax: '',
  psfMin: '', psfMax: '',
  sizeMin: '', sizeMax: '',
  bedroomMin: '', bedroomMax: '',
  floorMin: '', floorMax: '',
  bathMin: '', bathMax: '',
  districts: [],
  regions: [],
  tenures: [],
  categories: [],
  topYearMin: '', topYearMax: '',
};

interface ProjectResearchRow {
  id: string;
  projectName: string;
  projectCategory: string;
  matched: number;
  br1: number;
  br2: number;
  br3: number;
  br4: number;
  br5: number;
  ph: number;
  developer: string;
  totalUnits: number;
  soldUnits: number;
  districtLocation: string;
  district: string;
  region: string;
  nearestMrt: string;
  walkingTimeToMrt: string;
  tenure: string;
  topDate: string;
  completedDate: string;
  // Per-bedroom-type availability + pre-masked pricing strings from the backend.
  br1Avail: number; br1Price: string | null; br1Psf: string | null;
  br2Avail: number; br2Price: string | null; br2Psf: string | null;
  br3Avail: number; br3Price: string | null; br3Psf: string | null;
  br4Avail: number; br4Price: string | null; br4Psf: string | null;
  br5Avail: number; br5Price: string | null; br5Psf: string | null;
  phAvail: number;  phPrice: string | null;  phPsf: string | null;
  // Cheapest *matched* unit's masked price per bedroom bucket (powers the
  // optional "{N}BR fr." columns). `null` when no matched unit had a price.
  matchedBr1From: string | null;
  matchedBr2From: string | null;
  matchedBr3From: string | null;
  matchedBr4From: string | null;
  matchedBr5From: string | null;
  matchedPhFrom: string | null;
  /**
   * Project's geo coordinates (kept on the row so the page can reuse them
   * when computing the `distanceFromReferenceKm` column without a second
   * API round-trip). `null` when the project has no map data.
   */
  mapLatitude: number | null;
  mapLongitude: number | null;
  /**
   * Distance from the user-selected Reference Location to this project,
   * in km, rounded to 2 d.p. for display via Haversine. `null` when no
   * reference location is set or the project lacks coordinates.
   */
  distanceFromReferenceKm: number | null;
}

interface ProjectResearchUnitRow {
  id: string;
  /** Source projectId so the row can launch the Tower View dialog. */
  projectId: string;
  projectName: string;
  projectCategory: string;
  unitNumber: string;
  blockNumber: string;
  floor: number | string;
  stack: number | string;
  bedroom: number;
  bathroom: number;
  areaSqft: number;
  /** Pre-masked price string from the backend (e.g. `"1,XXX,XXX"`) or `null`. */
  price: string | null;
  /** DPS price not exposed by the public backend — always null (renders "-"). */
  priceDps: string | null;
  psf: string | null;
  /** Floor-plan image url (may be null when the unit has no plan). */
  floorplan: string | null;
  district: string;
  districtLocation: string;
  region: string;
  nearestMrt: string;
  walkingTimeToMrt: string;
  tenure: string;
  topYear: string;
  /** Distance from the Reference Location, inherited from the unit's project. */
  distanceFromReferenceKm: number | null;
}

// ---------------------------------------------------------------------------
// API → table-row mappers. Per-bedroom availability columns (`brXAvail` /
// `brXPsf`) are not exposed by the public backend (they live on the agent's
// internal price-book); we surface only the indicative low-price range.
// ---------------------------------------------------------------------------

/** Format `CompleteDate` like the agent's master-tool row — "Mar 2027" or '-'. */
const formatCompletedDate = (input: string | null | undefined): string => {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-SG', { year: 'numeric', month: 'short' });
};

/** Append the ` min` suffix when a walking-time value is present (agent parity). */
const formatWalkingTime = (value: string | number | null | undefined): string => {
  if (value == null) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  return `${trimmed} min`;
};

const toProjectRow = (p: PublicResearchProject): ProjectResearchRow => ({
  id: p.id,
  projectName: p.name ?? '',
  projectCategory: p.projectCategory ?? '',
  matched: p.matchedUnitCount,
  br1: p.matchedByBedroom.br1,
  br2: p.matchedByBedroom.br2,
  br3: p.matchedByBedroom.br3,
  br4: p.matchedByBedroom.br4,
  br5: p.matchedByBedroom.br5,
  ph: p.matchedByBedroom.ph,
  developer: p.developer ?? '',
  totalUnits: typeof p.totalUnits === 'number' ? p.totalUnits : 0,
  soldUnits: typeof p.soldUnits === 'number' ? p.soldUnits : 0,
  districtLocation: p.districtLocation ?? '',
  district: p.district ?? '',
  region: p.area ?? '',
  nearestMrt: p.nearestMrt ?? '',
  walkingTimeToMrt: formatWalkingTime(p.walkingTimeToMrt),
  tenure: p.tenure ?? '',
  topDate: p.topDate != null ? String(p.topDate) : '',
  completedDate: formatCompletedDate(typeof p.completedDate === 'string' ? p.completedDate : null),
  br1Avail: p.brStats?.br1?.availableUnits ?? 0,
  br1Price: p.brStats?.br1?.fromPrice ?? null,
  br1Psf: p.brStats?.br1?.fromPsf ?? null,
  br2Avail: p.brStats?.br2?.availableUnits ?? 0,
  br2Price: p.brStats?.br2?.fromPrice ?? null,
  br2Psf: p.brStats?.br2?.fromPsf ?? null,
  br3Avail: p.brStats?.br3?.availableUnits ?? 0,
  br3Price: p.brStats?.br3?.fromPrice ?? null,
  br3Psf: p.brStats?.br3?.fromPsf ?? null,
  br4Avail: p.brStats?.br4?.availableUnits ?? 0,
  br4Price: p.brStats?.br4?.fromPrice ?? null,
  br4Psf: p.brStats?.br4?.fromPsf ?? null,
  br5Avail: p.brStats?.br5?.availableUnits ?? 0,
  br5Price: p.brStats?.br5?.fromPrice ?? null,
  br5Psf: p.brStats?.br5?.fromPsf ?? null,
  phAvail: p.brStats?.ph?.availableUnits ?? 0,
  phPrice: p.brStats?.ph?.fromPrice ?? null,
  phPsf: p.brStats?.ph?.fromPsf ?? null,
  matchedBr1From: p.matchedLowestPriceByBedroom?.br1 ?? null,
  matchedBr2From: p.matchedLowestPriceByBedroom?.br2 ?? null,
  matchedBr3From: p.matchedLowestPriceByBedroom?.br3 ?? null,
  matchedBr4From: p.matchedLowestPriceByBedroom?.br4 ?? null,
  matchedBr5From: p.matchedLowestPriceByBedroom?.br5 ?? null,
  matchedPhFrom: p.matchedLowestPriceByBedroom?.ph ?? null,
  mapLatitude: p.mapLatitude,
  mapLongitude: p.mapLongitude,
  // Filled in lazily by `projectRowsWithDistance` once the user has
  // selected a Reference Location. Keep the row immutable here so the
  // raw API data stays cached across location changes.
  distanceFromReferenceKm: null,
});

const toUnitRow = (
  u: PublicResearchUnit,
  project?: ProjectResearchRow,
): ProjectResearchUnitRow => ({
  id: String(u.id),
  projectId: u.projectId ?? '',
  projectName: u.projectName,
  projectCategory: project?.projectCategory ?? '',
  unitNumber: u.unitNumber,
  blockNumber: u.blockNumber ?? '',
  floor: u.computedFloor ?? '',
  stack: u.computedStack ?? '',
  bedroom: u.bedroom,
  bathroom: u.bathroom,
  areaSqft: u.areaSqft,
  price: u.price ?? null,
  priceDps: null,
  psf: u.psf ?? null,
  floorplan: u.floorplan ?? null,
  district: project?.district ?? '',
  districtLocation: project?.districtLocation ?? '',
  region: project?.region ?? '',
  nearestMrt: project?.nearestMrt ?? '',
  walkingTimeToMrt: project?.walkingTimeToMrt ?? '',
  tenure: project?.tenure ?? '',
  topYear: project?.topDate ?? '',
  distanceFromReferenceKm: project?.distanceFromReferenceKm ?? null,
});

// Legacy mock data removed — pricing is now backend-masked strings, so the
// old numeric mocks no longer type-check. Live data only.

// ---------------------------------------------------------------------------
// Option sets — districts and project categories load from the public API
// on mount; regions and tenures are fixed enums (agent parity).
// ---------------------------------------------------------------------------

const REGION_OPTIONS = ['CCR', 'RCR', 'OCR'];
/** Matches agent values: `99`, `999-year leasehold`, `freehold`. */
const TENURE_OPTIONS = ['99', '999-year leasehold', 'freehold'] as const;

const formatTenureLabel = (opt: string): string =>
  opt === '99' ? '99-year' : opt === 'freehold' ? 'Freehold' : '999-year';

// ---------------------------------------------------------------------------
// Project View columns
// ---------------------------------------------------------------------------

const PROJECT_COLUMNS = [
  { key: 'projectName', label: 'Project Name', width: 200 },
  { key: 'projectCategory', label: 'Project Category', width: 140 },
  // New "Dist fr Ref. Loc" column — only meaningful once the user has
  // selected a Reference Location in the search criteria. Always declared
  // so the column toggles + sort/filter popover work uniformly; the cell
  // renderer falls back to "-" when distance is null.
  { key: 'distanceFromReferenceKm', label: 'Dist fr Ref. Loc', numeric: true, width: 130 },
  { key: 'matched', label: 'Matched', numeric: true, width: 90 },
  { key: 'br1', label: '1BR', numeric: true, width: 70 },
  { key: 'matchedBr1From', label: '1BR fr.', numeric: true, money: true, width: 120 },
  { key: 'br2', label: '2BR', numeric: true, width: 70 },
  { key: 'matchedBr2From', label: '2BR fr.', numeric: true, money: true, width: 120 },
  { key: 'br3', label: '3BR', numeric: true, width: 70 },
  { key: 'matchedBr3From', label: '3BR fr.', numeric: true, money: true, width: 120 },
  { key: 'br4', label: '4BR', numeric: true, width: 70 },
  { key: 'matchedBr4From', label: '4BR fr.', numeric: true, money: true, width: 120 },
  { key: 'br5', label: '5BR', numeric: true, width: 70 },
  { key: 'matchedBr5From', label: '5BR fr.', numeric: true, money: true, width: 120 },
  // PH / "PH fr." columns intentionally omitted from the public view — the
  // backend still computes the values (kept on the row), but they're never
  // rendered or exposed in the column-toggle popover.
  { key: 'developer', label: 'Developer', width: 160 },
  { key: 'totalUnits', label: 'Total Units', numeric: true, width: 90 },
  { key: 'soldUnits', label: 'Sold Units', numeric: true, width: 90 },
  { key: 'districtLocation', label: 'District/Location', width: 170 },
  { key: 'district', label: 'District', width: 90 },
  { key: 'region', label: 'Region', width: 90 },
  { key: 'nearestMrt', label: 'Nearest MRT', width: 150 },
  { key: 'walkingTimeToMrt', label: 'Walk to MRT', width: 120 },
  { key: 'tenure', label: 'Tenure', width: 130 },
  { key: 'topDate', label: 'TOP Year', width: 100, numeric: true },
  { key: 'completedDate', label: 'Completed Date', width: 130 },
  { key: 'br1Avail', label: '1BR Avail', numeric: true, width: 90 },
  { key: 'br1Price', label: '1BR Price', numeric: true, money: true, width: 130 },
  { key: 'br1Psf', label: '1BR Psf', numeric: true, money: true, width: 110 },
  { key: 'br2Avail', label: '2BR Avail', numeric: true, width: 90 },
  { key: 'br2Price', label: '2BR Price', numeric: true, money: true, width: 130 },
  { key: 'br2Psf', label: '2BR Psf', numeric: true, money: true, width: 110 },
  { key: 'br3Avail', label: '3BR Avail', numeric: true, width: 90 },
  { key: 'br3Price', label: '3BR Price', numeric: true, money: true, width: 130 },
  { key: 'br3Psf', label: '3BR Psf', numeric: true, money: true, width: 110 },
  { key: 'br4Avail', label: '4BR Avail', numeric: true, width: 90 },
  { key: 'br4Price', label: '4BR Price', numeric: true, money: true, width: 130 },
  { key: 'br4Psf', label: '4BR Psf', numeric: true, money: true, width: 110 },
  { key: 'br5Avail', label: '5BR Avail', numeric: true, width: 90 },
  { key: 'br5Price', label: '5BR Price', numeric: true, money: true, width: 130 },
  { key: 'br5Psf', label: '5BR Psf', numeric: true, money: true, width: 110 },
  { key: 'phAvail', label: 'PH Avail', numeric: true, width: 90 },
  { key: 'phPrice', label: 'PH Price', numeric: true, money: true, width: 130 },
  { key: 'phPsf', label: 'PH Psf', numeric: true, money: true, width: 110 },
] as const;

type ProjectColumnKey = typeof PROJECT_COLUMNS[number]['key'];

/**
 * Columns that start hidden in the Project View — they're available via the
 * "Columns" popover (unchecked) but won't render until the user opts in.
 * Mirrors the agent's `MASTER_TOOL_PROJECT_DEFAULT_HIDDEN_COLUMNS` set for
 * the matched-from price columns ("1BR fr.", "2BR fr.", ..., "PH fr.").
 */
const PROJECT_DEFAULT_HIDDEN_COLUMNS: ReadonlySet<ProjectColumnKey> = new Set<ProjectColumnKey>([
  'matchedBr1From', 'matchedBr2From', 'matchedBr3From',
  'matchedBr4From', 'matchedBr5From',
]);

/**
 * Columns visible under the "Project details only" preset. Mirrors the agent's
 * Master Tool quick-toggle: hides every matched-count and per-bedroom price /
 * availability column.
 */
const PROJECT_DETAILS_ONLY_COLUMNS: ReadonlyArray<ProjectColumnKey> = [
  'projectName', 'projectCategory', 'distanceFromReferenceKm', 'developer',
  'totalUnits', 'soldUnits', 'districtLocation', 'district', 'region',
  'nearestMrt', 'walkingTimeToMrt', 'tenure', 'topDate', 'completedDate',
];

/** Labels shown in the columns menu (slightly different from header labels). */
const PROJECT_COLUMN_MENU_LABELS: Partial<Record<ProjectColumnKey, string>> = {
  matched: 'Matched',
  br1: 'Matched 1BR',
  br2: 'Matched 2BR',
  br3: 'Matched 3BR',
  br4: 'Matched 4BR',
  br5: 'Matched 5BR',
  // 'ph' label intentionally omitted — the PH column is not exposed in the
  // public view.
};

const UNIT_COLUMNS = [
  { key: 'projectName', label: 'Project Name', width: 200 },
  { key: 'projectCategory', label: 'Project Category', width: 140 },
  { key: 'unitNumber', label: 'Unit', width: 100 },
  { key: 'blockNumber', label: 'Block', width: 90 },
  { key: 'floor', label: 'Floor', numeric: true, width: 80 },
  { key: 'stack', label: 'Stack', numeric: true, width: 80 },
  { key: 'bedroom', label: 'Bed', numeric: true, width: 70 },
  { key: 'bathroom', label: 'Bath', numeric: true, width: 70 },
  { key: 'areaSqft', label: 'Area (sqft)', numeric: true, width: 110 },
  { key: 'price', label: 'Price/NPS $', numeric: true, money: true, width: 130 },
  { key: 'priceDps', label: 'DPS $', numeric: true, money: true, width: 110 },
  { key: 'psf', label: 'PSF $', numeric: true, money: true, width: 110 },
  { key: 'district', label: 'District', width: 90 },
  { key: 'districtLocation', label: 'District/Location', width: 170 },
  { key: 'region', label: 'Region', width: 90 },
  { key: 'nearestMrt', label: 'Nearest MRT', width: 150 },
  { key: 'walkingTimeToMrt', label: 'Walking Time', width: 110, numeric: true },
  { key: 'tenure', label: 'Tenure', width: 130 },
  { key: 'topYear', label: 'TOP Year', width: 100, numeric: true },
  // Inherited from the unit's project — same column the project view shows.
  { key: 'distanceFromReferenceKm', label: 'Dist fr Ref. Loc', numeric: true, width: 130 },
] as const;

type UnitColumnKey = typeof UNIT_COLUMNS[number]['key'];

/**
 * Render a backend-masked price string (e.g. `"1,XXX,XXX"`) as a dollar
 * display. The backend already applied `maskNumber` (see
 * `backend/src/routes/superPickerPublic.ts`), so we only prefix `$`.
 *
 *   "1,XXX,XXX" -> "$1,XXX,XXX"
 *   ""          -> "-"
 *   null / undef -> "-"
 */
const formatPrice = (value: string | null | undefined): string => {
  if (value == null) return '-';
  const trimmed = String(value).trim();
  if (!trimmed) return '-';
  return `$${trimmed}`;
};
const formatPsf = formatPrice;

/**
 * Render the `Dist fr Ref. Loc` column value as `"3.70 km"`. Always emits
 * two decimals so a 3.7 km result reads as `3.70 km` (matches the agent's
 * `referenceDistanceKm` formatter). Returns `'-'` when the row has no
 * reference distance (no location selected, or the project lacks
 * coordinates).
 */
const formatReferenceDistanceKm = (value: unknown): string => {
  if (value == null) return '-';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${n.toFixed(2)} km`;
};

/**
 * Derive a coarse ordering value from a backend-masked price string. The
 * backend masking keeps the first digit and the digit count (e.g.
 * `"1,XXX,XXX"`), so we can sort / range-filter approximately by treating the
 * masked value as `leadingDigit * 10^(digitCount - 1)`:
 *
 *   "1,XXX,XXX" → 1_000_000
 *   "8XX"       → 800
 *   "2,XXX"     → 2_000
 *   null / "-"  → NaN  (rows with missing prices sort last and never satisfy
 *                       a min / max filter)
 *
 * This is intentionally a *lower bound* of the true value — adequate for
 * coarse "show me units below $2M" filtering even though exact pricing is
 * never exposed to the frontend.
 */
const maskedPriceOrderKey = (value: unknown): number => {
  if (value == null) return NaN;
  const raw = String(value).replace(/[,\s$]/g, '');
  if (!raw) return NaN;
  const first = raw.charCodeAt(0);
  // require a leading digit 0-9 (mask never emits '-', so 0 won't appear)
  if (first < 48 || first > 57) return NaN;
  let digitCount = 0;
  for (const ch of raw) {
    if ((ch >= '0' && ch <= '9') || ch === 'X' || ch === 'x') digitCount += 1;
  }
  if (digitCount === 0) return NaN;
  return (first - 48) * Math.pow(10, digitCount - 1);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ProjectResearchPageProps {
  onBack: () => void;
  /**
   * Hide the back-arrow icon button without removing it from the tree. Used
   * by share-link recipients pinned to Project Research — they have no
   * Site Plans listing to return to. We `display: none` rather than
   * unmounting so the button code stays in place for future flows.
   */
  hideBackButton?: boolean;
}

const ProjectResearchPage: React.FC<ProjectResearchPageProps> = ({
  onBack,
  hideBackButton = false,
}) => {
  // Fire `project_research_tab_viewed` once on mount. Mirrors the agent's
  // `logProjectResearchTabViewed` call when the tab is opened in the
  // master tool, attached implicitly to the share context via the
  // public-analytics module.
  useEffect(() => {
    logPublicProjectResearchTabViewed();
  }, []);

  const mobileOptimizedStyles = useMobileOptimizedStyles();
  const [filters, setFilters] = useState<ResearchFilters>(EMPTY_FILTERS);
  const [view, setView] = useState<'project' | 'unit'>('project');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(25);
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);

  const [visibleProjectCols, setVisibleProjectCols] = useState<Set<ProjectColumnKey>>(
    // Start with every column visible *except* the ones explicitly marked
    // hidden-by-default (currently the "{N}BR fr." matched-from prices),
    // matching the agent-side default in `SuperPickerApp.tsx`.
    () => new Set<ProjectColumnKey>(
      PROJECT_COLUMNS
        .map((c) => c.key as ProjectColumnKey)
        .filter((key) => !PROJECT_DEFAULT_HIDDEN_COLUMNS.has(key)),
    ),
  );
  const [visibleUnitCols, setVisibleUnitCols] = useState<Set<UnitColumnKey>>(
    () => new Set(UNIT_COLUMNS.map((c) => c.key)),
  );

  // ---- Per-column sort + filter (agent parity with the Master Tool popover)
  type SortDirection = 'default' | 'asc' | 'desc';
  type RangeValue = { min: string; max: string };
  type ColumnPopoverAnchor = {
    element: HTMLElement;
    column: string;
    view: 'project' | 'unit';
  };
  const [projectSortConfig, setProjectSortConfig] = useState<{ column: ProjectColumnKey | null; direction: SortDirection }>({
    column: null,
    direction: 'default',
  });
  const [unitSortConfig, setUnitSortConfig] = useState<{ column: UnitColumnKey | null; direction: SortDirection }>({
    column: null,
    direction: 'default',
  });
  const [projectTextFilters, setProjectTextFilters] = useState<Record<string, string>>({});
  const [projectRangeFilters, setProjectRangeFilters] = useState<Record<string, RangeValue>>({});
  const [projectMultiFilters, setProjectMultiFilters] = useState<Record<string, string[]>>({});
  const [unitTextFilters, setUnitTextFilters] = useState<Record<string, string>>({});
  const [unitRangeFilters, setUnitRangeFilters] = useState<Record<string, RangeValue>>({});
  const [unitMultiFilters, setUnitMultiFilters] = useState<Record<string, string[]>>({});
  const [columnPopoverAnchor, setColumnPopoverAnchor] = useState<ColumnPopoverAnchor | null>(null);

  const closeColumnPopover = () => setColumnPopoverAnchor(null);

  // ---- Per-project "Matched units" expansion (clicking the matched/BR/PH cells).
  // Mirrors `masterToolProjectExpand` in `SuperPickerApp.tsx`.
  type MatchedExpandBucket = 'total' | 'br1' | 'br2' | 'br3' | 'br4' | 'br5' | 'ph';
  const [matchedExpand, setMatchedExpand] = useState<{
    projectId: string;
    bucket: MatchedExpandBucket;
  } | null>(null);
  const [matchedUnitsByProject, setMatchedUnitsByProject] = useState<Record<string, PublicResearchUnit[]>>({});
  const [matchedUnitsLoading, setMatchedUnitsLoading] = useState<Record<string, boolean>>({});
  const [matchedUnitsError, setMatchedUnitsError] = useState<Record<string, string>>({});

  /** Decide if a unit row falls into the requested bucket (total / brN / ph). */
  const unitMatchesBucket = (unit: PublicResearchUnit, bucket: MatchedExpandBucket): boolean => {
    if (bucket === 'total') return true;
    const desc = (unit.unitTypeDescription || '').toLowerCase();
    const isPh = desc.includes('penthouse') || /\bph\b/.test(desc);
    if (bucket === 'ph') return isPh;
    const expected = Number(bucket.replace('br', ''));
    if (!Number.isFinite(expected)) return false;
    if (isPh) return false;
    return Number(unit.bedroom) === expected;
  };

  /** Floor-plan modal triggered from the project-name "image" icon. */
  const [floorPlanUnit, setFloorPlanUnit] = useState<PublicUnitForMenu | null>(null);
  /** Anchor for the per-row unit-actions popup (mirrors `sectionUnitMenuAnchor`). */
  const [unitMenuAnchor, setUnitMenuAnchor] = useState<UnitMenuAnchor | null>(null);
  /** Compare cart + modal — same shape used by `ProjectDetailPage`. */
  const compare = useFloorPlanCompare();

  const handleOpenUnitMenu = (unit: PublicUnitForMenu, e: React.MouseEvent<HTMLElement>) => {
    setUnitMenuAnchor({ unit, mouseX: e.clientX, mouseY: e.clientY });
  };
  const handleUnitClick = (unit: PublicUnitForMenu, mouseX: number, mouseY: number) => {
    setUnitMenuAnchor({ unit, mouseX, mouseY });
  };
  const handleViewFloorPlanFromMenu = (unit: PublicUnitForMenu) => {
    logPublicFloorPlanOpened({
      projectName: unit.projectName ?? null,
      projectId: unit.projectId ?? null,
      unitNumber: unit.unitNumber ?? null,
      source: 'project_research_page',
    });
    setFloorPlanUnit(unit);
  };
  const handleToggleCompare = (unit: PublicUnitForMenu) => { compare.toggle(unit); };

  // ----- Per-row reveal-prices flow -------------------------------------
  // Same pattern as `ProjectDetailPage` but driven by the menu unit's own
  // `projectId` field (search results span many projects). Synthetic key
  // is recomputed from `(blockNumber, unitNumber)` because the menu's
  // `unitKey` carries the raw DB id for compare-cart parity with the
  // agent shell.
  const [revealPricesUnit, setRevealPricesUnit] = useState<PublicUnitForMenu | null>(null);
  const { store: revealedStore, setRevealed } = useRevealedPrices();
  const revealPrices = useTokenSpend<{
    units: Record<string, RevealedTowerViewUnitPrices>;
    projectId: string;
  }>({
    title: 'Reveal prices',
    actionLabel: 'Reveal',
    description: revealPricesUnit ? (
      <>
        Spend <strong>1 token</strong> to unmask the prices for unit{' '}
        <strong>{revealPricesUnit.unitNumber}</strong> at{' '}
        <strong>{revealPricesUnit.projectName}</strong>?
      </>
    ) : undefined,
    spend: async () => {
      const uuid = getPublicShareUuid();
      if (!uuid) throw new Error('Missing share context.');
      if (!revealPricesUnit?.projectId) throw new Error('Missing project context.');
      const synthetic = buildPublicUnitSyntheticKey(
        revealPricesUnit.blockNumber,
        revealPricesUnit.unitNumber,
      );
      const r = await revealSection(uuid, {
        section: 'towerView',
        projectId: revealPricesUnit.projectId,
        unitKey: synthetic,
      });
      if (r.section !== 'towerView') {
        throw new Error('Unexpected reveal response.');
      }
      return {
        remainingTokens: r.remainingTokens,
        maxTokens: r.maxTokens,
        data: { units: r.units, projectId: r.projectId },
      };
    },
    onConfirmed: ({ data }) => {
      if (!data) return;
      const existing = revealedStore.towerView[data.projectId] ?? {};
      setRevealed('towerView', data.projectId, { ...existing, ...data.units });
      setRevealPricesUnit(null);
    },
  });
  const handleRevealPricesRequest = useCallback((unit: PublicUnitForMenu) => {
    setRevealPricesUnit(unit);
    revealPrices.request();
  }, [revealPrices]);
  const canRevealPrices = Boolean(getPublicShareUuid());
  const isUnitRevealed = useCallback(
    (unit: PublicUnitForMenu | null | undefined): boolean => {
      if (!unit?.projectId) return false;
      const store = revealedStore.towerView[unit.projectId];
      if (!store) return false;
      if (unit.unitKey && store[unit.unitKey]) return true;
      const synthetic = buildPublicUnitSyntheticKey(unit.blockNumber, unit.unitNumber);
      return Boolean(store[synthetic]);
    },
    [revealedStore],
  );

  // ---- Tower View dialog (per-row, agent parity with `handleOpenTowerView`).
  const [towerViewDialog, setTowerViewDialog] = useState<{
    projectId: string;
    projectName: string;
  } | null>(null);
  // Site-plan filters lifted out of `SitePlanSectionDetail` so the
  // sibling `TowerViewSection` shares the same Bedroom + Unit Type
  // selection (mirrors the agent-side `sitePlanBedroomFilter` /
  // `sitePlanUnitTypeFilter` plumbing).
  const [towerViewBedroomFilter, setTowerViewBedroomFilter] = useState<number[]>([]);
  const [towerViewUnitTypeFilter, setTowerViewUnitTypeFilter] = useState<string[]>([]);
  // Shared Stack selection so the Tower View's Stack dropdown also filters
  // + pulses the matching site-plan dots (agent parity with
  // `sitePlanStackFilter`).
  const [towerViewStackFilter, setTowerViewStackFilter] = useState<string[]>([]);
  const [towerViewUnits, setTowerViewUnits] = useState<PublicTowerViewUnit[]>([]);
  const [towerViewSitePlan, setTowerViewSitePlan] = useState<PublicSitePlanPayload | null>(null);
  const [towerViewStackSold, setTowerViewStackSold] = useState<Record<string, number> | null>(null);
  const [towerViewDetails, setTowerViewDetails] = useState<PublicProjectDetails | null>(null);
  const [towerViewLoading, setTowerViewLoading] = useState(false);
  const [towerViewError, setTowerViewError] = useState<string | null>(null);
  /**
   * Drives which stack dots pulse in the Tower View dialog (agent parity with
   * `sitePlanBlinkingStacks`). Either an explicit stack list (unit view / a
   * specific matched unit) or a list of bedrooms — bedrooms are resolved
   * against `towerViewStackInfos` at render time.
   */
  const [towerViewBlink, setTowerViewBlink] = useState<
    { kind: 'stacks'; stacks: string[] } | { kind: 'bedrooms'; bedrooms: number[] } | null
  >(null);

  /**
   * Bedrooms currently selected in the project search filter — used as the
   * default blink set when opening the Tower View from a project-view row.
   */
  const projectFilterBedrooms = useMemo<number[]>(() => {
    const min = Number(filters.bedroomMin);
    const max = Number(filters.bedroomMax);
    const hasMin = Number.isFinite(min) && min > 0;
    const hasMax = Number.isFinite(max) && max > 0;
    if (!hasMin && !hasMax) return [];
    const lo = hasMin ? min : (hasMax ? max : 0);
    const hi = hasMax ? max : (hasMin ? min : 0);
    const out: number[] = [];
    for (let b = Math.min(lo, hi); b <= Math.max(lo, hi); b += 1) out.push(b);
    return out;
  }, [filters.bedroomMin, filters.bedroomMax]);

  const handleOpenTowerView = (
    projectId: string,
    projectName: string,
    blink?: { kind: 'stacks'; stacks: string[] } | { kind: 'bedrooms'; bedrooms: number[] } | null,
  ) => {
    logPublicTowerViewOpened({
      projectId,
      projectName,
      source: 'project_research_page',
    });
    setTowerViewDialog({ projectId, projectName });
    setTowerViewBlink(blink ?? null);
    setTowerViewUnits([]);
    setTowerViewSitePlan(null);
    setTowerViewStackSold(null);
    setTowerViewDetails(null);
    setTowerViewError(null);
    setTowerViewLoading(true);
    Promise.allSettled([
      fetchPublicTowerViewUnits(projectId),
      fetchPublicSitePlan(projectId),
      fetchPublicStackSoldPercentages(projectId),
      fetchPublicProjectDetails(projectId),
    ])
      .then(([towerR, sitePlanR, soldR, detailsR]) => {
        if (towerR.status === 'fulfilled') {
          setTowerViewUnits(towerR.value.units);
        } else {
          setTowerViewError(
            towerR.reason instanceof Error ? towerR.reason.message : 'Failed to load tower view',
          );
        }
        if (sitePlanR.status === 'fulfilled') setTowerViewSitePlan(sitePlanR.value);
        if (soldR.status === 'fulfilled') setTowerViewStackSold(soldR.value);
        if (detailsR.status === 'fulfilled') setTowerViewDetails(detailsR.value);
      })
      .finally(() => setTowerViewLoading(false));
  };

  const closeTowerView = () => {
    setTowerViewDialog(null);
    setTowerViewUnits([]);
    setTowerViewSitePlan(null);
    setTowerViewStackSold(null);
    setTowerViewDetails(null);
    setTowerViewError(null);
    setTowerViewLoading(false);
  };

  // Derived site-plan props for the dialog (mirrors `ProjectDetailPage`).
  const towerViewSitePlanBlocks = useMemo<SitePlanBlock[]>(() => {
    if (!towerViewSitePlan) return [];
    return towerViewSitePlan.blocks
      .filter((b) => b.stack)
      .map((b) => ({
        blockNumber: b.blockNumber ?? '',
        stack: String(b.stack),
        facing: b.facing ?? undefined,
        x: b.x,
        y: b.y,
        width: b.width,
        height: b.height,
      }));
  }, [towerViewSitePlan]);
  const towerViewSitePlanBounds: SitePlanBounds | null = towerViewSitePlan?.bounds ?? null;
  const towerViewSitePlanImageUrl: string | null = towerViewSitePlan?.imageUrl ?? null;
  const towerViewStackInfos = useMemo<StackUnitInfo[]>(() => {
    if (towerViewUnits.length === 0) return [];
    const map = new Map<string, StackUnitInfo>();
    for (const u of towerViewUnits) {
      const m = (u.unitNumber || '').replace(/^#/, '').match(/^\d+\s*-\s*(\d+)$/);
      if (!m) continue;
      const stack = m[1];
      if (map.has(stack)) continue;
      map.set(stack, {
        stack,
        bedroom: Number.isFinite(u.bedroom) ? u.bedroom : 0,
        unitType: u.unitTypeDescription || u.unitType || '',
      });
    }
    return Array.from(map.values());
  }, [towerViewUnits]);

  /**
   * Resolves `towerViewBlink` into the concrete stack-id set consumed by
   * `SitePlanSectionDetail`. For `bedrooms`, every stack whose `bedroom`
   * matches is included (agent parity with `sitePlanBlinkingStacks`).
   */
  const towerViewBlinkingStacks = useMemo<Set<string> | null>(() => {
    if (!towerViewBlink) return null;
    if (towerViewBlink.kind === 'stacks') {
      return new Set(towerViewBlink.stacks.map((s) => String(s).trim()));
    }
    if (towerViewStackInfos.length === 0) return null;
    const wanted = new Set(towerViewBlink.bedrooms);
    const out = new Set<string>();
    for (const info of towerViewStackInfos) {
      if (wanted.has(info.bedroom)) out.add(String(info.stack).trim());
    }
    return out.size > 0 ? out : null;
  }, [towerViewBlink, towerViewStackInfos]);

  /** Render a hamburger / arrow indicator like the agent's `renderSortIndicatorIcon`. */
  const renderSortIndicatorIcon = (direction: SortDirection) => {
    if (direction === 'default') return <MenuIcon fontSize="small" />;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        {direction === 'asc'
          ? <ArrowUpwardIcon sx={{ fontSize: '0.85rem' }} />
          : <ArrowDownwardIcon sx={{ fontSize: '0.85rem' }} />}
        <MenuIcon fontSize="small" />
      </Box>
    );
  };

  const getColumnSortDirection = (mode: 'project' | 'unit', column: string): SortDirection => {
    const cfg = mode === 'project' ? projectSortConfig : unitSortConfig;
    return cfg.column === column ? cfg.direction : 'default';
  };

  const hasActiveColumnFilter = (mode: 'project' | 'unit', column: string): boolean => {
    const multi = (mode === 'project' ? projectMultiFilters : unitMultiFilters)[column];
    if (Array.isArray(multi) && multi.length > 0) return true;
    const text = (mode === 'project' ? projectTextFilters : unitTextFilters)[column] ?? '';
    if (text.trim() !== '') return true;
    const range = (mode === 'project' ? projectRangeFilters : unitRangeFilters)[column];
    if (!range) return false;
    return (range.min ?? '').trim() !== '' || (range.max ?? '').trim() !== '';
  };

  const getColumnIconButtonSx = (mode: 'project' | 'unit', column: string) => {
    const active = hasActiveColumnFilter(mode, column);
    return {
      p: 0.5,
      color: active ? 'var(--color-accent)' : 'var(--color-text-strong)',
      '&:hover': {
        bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.1)',
        color: 'var(--color-accent)',
      },
    };
  };

  // ---- API-backed data + option feeds
  const [projectRows, setProjectRows] = useState<ProjectResearchRow[]>([]);
  const [unitRows, setUnitRows] = useState<ProjectResearchUnitRow[]>([]);
  const [totalMatchedUnits, setTotalMatchedUnits] = useState(0);
  const [totalUnits, setTotalUnits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [districtOpts, setDistrictOpts] = useState<PublicDistrictOption[]>([]);
  const [categoryOpts, setCategoryOpts] = useState<PublicProjectCategoryOption[]>([]);

  // ---- Reference Location + radius (agent parity with the Master Tool form).
  // Stored in component state rather than `filters` because it's a pure
  // client-side post-filter (no public API support); the radius narrows the
  // already-fetched project rows via Haversine distance against
  // `mapLatitude` / `mapLongitude`.
  const [locationInput, setLocationInput] = useState('');
  const [locationOptions, setLocationOptions] = useState<PublicOneMapSearchResult[]>([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<PublicOneMapSearchResult | null>(null);
  const [radiusKm, setRadiusKm] = useState('');
  // Resolved coordinates of the selected location (memoised so downstream
  // useMemos don't re-run on every keystroke).
  const referenceCoords = useMemo<{ lat: number; lng: number } | null>(() => {
    if (!selectedLocation) return null;
    return parseOnemapCoordinates(selectedLocation);
  }, [selectedLocation]);
  const hasReferenceLocation = referenceCoords != null;
  const radiusKmNumeric = useMemo(() => {
    const n = Number((radiusKm ?? '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [radiusKm]);
  const isRadiusMissing = hasReferenceLocation && radiusKmNumeric == null;

  /** Reset the location autocomplete + radius. Mirrors `clearMasterToolLocationFilters`. */
  const clearLocationFilters = useCallback(() => {
    setLocationInput('');
    setLocationOptions([]);
    setSelectedLocation(null);
    setRadiusKm('');
  }, []);

  // Debounced OneMap search on input changes — same 300 ms cadence the
  // agent uses, with a `cancelled` flag to discard stale responses if the
  // user keeps typing.
  useEffect(() => {
    const q = locationInput.trim();
    if (!q) {
      setLocationOptions([]);
      return;
    }
    let cancelled = false;
    setLocationSearching(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchPublicOnemapLocations(q);
        if (!cancelled) setLocationOptions(results);
      } catch {
        if (!cancelled) setLocationOptions([]);
      } finally {
        if (!cancelled) setLocationSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [locationInput]);

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([fetchPublicDistricts(), fetchPublicProjectCategories()]).then(
      ([districtsResult, categoriesResult]) => {
        if (!mounted) return;
        if (districtsResult.status === 'fulfilled') setDistrictOpts(districtsResult.value);
        if (categoriesResult.status === 'fulfilled') setCategoryOpts(categoriesResult.value);
      },
    );
    return () => { mounted = false; };
  }, []);

  /** Translate the form's filter state into the API filter shape. */
  const toApiFilters = (): PublicResearchFilters => ({
    priceMin: filters.priceMin || undefined,
    priceMax: filters.priceMax || undefined,
    psfMin: filters.psfMin || undefined,
    psfMax: filters.psfMax || undefined,
    sizeMin: filters.sizeMin || undefined,
    sizeMax: filters.sizeMax || undefined,
    bedroomMin: filters.bedroomMin || undefined,
    bedroomMax: filters.bedroomMax || undefined,
    floorMin: filters.floorMin || undefined,
    floorMax: filters.floorMax || undefined,
    bathMin: filters.bathMin || undefined,
    bathMax: filters.bathMax || undefined,
    districts: filters.districts.length > 0 ? filters.districts : undefined,
    regions: filters.regions.length > 0 ? filters.regions : undefined,
    tenure: filters.tenures.length > 0 ? filters.tenures : undefined,
    projectCategories: filters.categories.length > 0 ? filters.categories : undefined,
    topYearMin: filters.topYearMin || undefined,
    topYearMax: filters.topYearMax || undefined,
  });

  const handleFilter = <K extends keyof ResearchFilters>(key: K, value: ResearchFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    clearLocationFilters();
    setProjectRows([]);
    setUnitRows([]);
    setTotalMatchedUnits(0);
    setTotalUnits(0);
    setSelectedIds(new Set());
    setProjectTextFilters({});
    setProjectRangeFilters({});
    setProjectMultiFilters({});
    setUnitTextFilters({});
    setUnitRangeFilters({});
    setUnitMultiFilters({});
    setMatchedExpand(null);
    setMatchedUnitsByProject({});
    setMatchedUnitsLoading({});
    setMatchedUnitsError({});
    setPage(0);
    setHasSearched(false);
    setError(null);
  };

  /**
   * Toggle the matched-units expansion for a (project, bucket) pair. If the
   * project's units have not yet been loaded, fetch them with the current
   * search filters scoped to this project name.
   */
  const handleMatchedExpandClick = async (
    row: ProjectResearchRow,
    bucket: MatchedExpandBucket,
    count: number,
  ) => {
    if (count <= 0) return;
    const isOpen = matchedExpand?.projectId === row.id && matchedExpand?.bucket === bucket;
    if (isOpen) {
      setMatchedExpand(null);
      return;
    }
    setMatchedExpand({ projectId: row.id, bucket });
    if (matchedUnitsByProject[row.id]) return;
    setMatchedUnitsLoading((prev) => ({ ...prev, [row.id]: true }));
    setMatchedUnitsError((prev) => {
      const { [row.id]: _omit, ...rest } = prev;
      return rest;
    });
    try {
      const apiFilters = toApiFilters();
      const resp = await searchPublicUnits({
        filters: apiFilters,
        page: 1,
        pageSize: 500,
        unitProjectNames: [row.projectName],
      });
      setMatchedUnitsByProject((prev) => ({ ...prev, [row.id]: resp.items }));
    } catch (err) {
      setMatchedUnitsError((prev) => ({
        ...prev,
        [row.id]: err instanceof Error ? err.message : 'Failed to load units',
      }));
    } finally {
      setMatchedUnitsLoading((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const runSearch = async (mode: 'project' | 'unit') => {
    setLoading(true);
    setError(null);
    setPage(0);
    setHasSearched(true);
    // Cached matched-unit panels are bound to the previous filter set; drop them.
    setMatchedExpand(null);
    setMatchedUnitsByProject({});
    setMatchedUnitsLoading({});
    setMatchedUnitsError({});
    try {
      const apiFilters = toApiFilters();
      const projectResp = await searchPublicProjects(apiFilters);
      const projectRowsNext = projectResp.projects.map(toProjectRow);
      setProjectRows(projectRowsNext);
      setTotalMatchedUnits(projectResp.totalMatchedUnits);
      if (mode === 'unit') {
        const unitResp = await searchPublicUnits({ filters: apiFilters, page: 1, pageSize: 100 });
        const projectByName = new Map(projectRowsNext.map((p) => [p.projectName, p]));
        const unitRowsNext = unitResp.items.map((u) =>
          toUnitRow(u, projectByName.get(u.projectName)),
        );
        setUnitRows(unitRowsNext);
        setTotalUnits(unitResp.totalUnits);
      } else {
        setUnitRows([]);
        setTotalUnits(0);
        setSelectedIds(new Set(projectRowsNext.map((p) => p.id)));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to search');
      setProjectRows([]);
      setUnitRows([]);
      setTotalMatchedUnits(0);
      setTotalUnits(0);
    } finally {
      setLoading(false);
    }
  };

  // Search costs 1 token. We prompt with the reusable `TokenSpendDialog`
  // first; on confirm, the dialog calls the backend `consume` endpoint,
  // updates the header chip, then runs `runSearch` against the current
  // view (project vs unit) via the closure below.
  const tokenSpend = useTokenSpend({
    title: 'Confirm search',
    actionLabel: 'Search',
    description: (
      <>
        Running a search will use <strong>1 token</strong>. This will deduct
        from your remaining tokens. Continue?
      </>
    ),
    onConfirmed: () => runSearch(view),
  });
  const handleSearch = () => {
    // Mirror the agent gate — a Reference Location requires a positive
    // radius to make sense; without one the radius post-filter would
    // either drop everything (radius 0) or have no effect at all.
    if (hasReferenceLocation && radiusKmNumeric == null) {
      setError('Please enter a valid Radius (km) before searching.');
      return;
    }
    tokenSpend.request();
  };

  // Switching to Unit View after a project search lazily fetches the unit list.
  useEffect(() => {
    if (view !== 'unit' || !hasSearched || unitRows.length > 0 || loading) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const unitResp = await searchPublicUnits({
          filters: toApiFilters(),
          page: 1,
          pageSize: 100,
        });
        if (cancelled) return;
        const projectByName = new Map(projectRows.map((p) => [p.projectName, p]));
        const unitRowsNext = unitResp.items.map((u) =>
          toUnitRow(u, projectByName.get(u.projectName)),
        );
        setUnitRows(unitRowsNext);
        setTotalUnits(unitResp.totalUnits);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load units');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // ---- Apply header filters + sort to the API-backed rows.
  const applyFilters = <T,>(
    rows: T[],
    columns: ReadonlyArray<{ key: string; numeric?: boolean; money?: boolean }>,
    textFilters: Record<string, string>,
    rangeFilters: Record<string, RangeValue>,
    multiFilters: Record<string, string[]> = {},
  ): T[] => {
    const numericKeys = new Set(columns.filter((c) => c.numeric).map((c) => c.key));
    // Money columns are backend-masked strings — they still range-filter, but
    // we approximate the value via `maskedPriceOrderKey`.
    const moneyKeys = new Set(columns.filter((c) => c.money).map((c) => c.key));
    return rows.filter((row) => {
      const indexed = row as unknown as Record<string, unknown>;
      for (const [key, values] of Object.entries(multiFilters)) {
        if (!Array.isArray(values) || values.length === 0) continue;
        const cell = indexed[key];
        const cellStr = cell == null ? '' : String(cell);
        if (!values.includes(cellStr)) return false;
      }
      for (const [key, raw] of Object.entries(textFilters)) {
        const q = (raw ?? '').trim().toLowerCase();
        if (!q || numericKeys.has(key) || moneyKeys.has(key)) continue;
        // If a multi-select is already active for this column, skip the text filter.
        if (Array.isArray(multiFilters[key]) && multiFilters[key].length > 0) continue;
        const cell = indexed[key];
        const cellStr = cell == null ? '' : String(cell).toLowerCase();
        if (!cellStr.includes(q)) return false;
      }
      for (const [key, range] of Object.entries(rangeFilters)) {
        if (!range || (!numericKeys.has(key) && !moneyKeys.has(key))) continue;
        const minStr = (range.min ?? '').replace(/,/g, '').trim();
        const maxStr = (range.max ?? '').replace(/,/g, '').trim();
        if (!minStr && !maxStr) continue;
        const value = moneyKeys.has(key)
          ? maskedPriceOrderKey(indexed[key])
          : Number(indexed[key]);
        if (!Number.isFinite(value)) return false;
        if (minStr !== '' && value < Number(minStr)) return false;
        if (maxStr !== '' && value > Number(maxStr)) return false;
      }
      return true;
    });
  };

  const applySort = <T,>(
    rows: T[],
    columns: ReadonlyArray<{ key: string; numeric?: boolean; money?: boolean }>,
    config: { column: string | null; direction: SortDirection },
  ): T[] => {
    if (!config.column || config.direction === 'default') return rows;
    const col = columns.find((c) => c.key === config.column);
    if (!col) return rows;
    const dir = config.direction === 'asc' ? 1 : -1;
    const numeric = !!col.numeric;
    const money = !!col.money;
    return [...rows].sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[col.key];
      const bv = (b as unknown as Record<string, unknown>)[col.key];
      if (numeric || money) {
        const an = money ? maskedPriceOrderKey(av) : Number(av);
        const bn = money ? maskedPriceOrderKey(bv) : Number(bv);
        const aValid = Number.isFinite(an);
        const bValid = Number.isFinite(bn);
        if (!aValid && !bValid) return 0;
        if (!aValid) return 1;
        if (!bValid) return -1;
        return (an - bn) * dir;
      }
      const as = (av == null ? '' : String(av)).toLowerCase();
      const bs = (bv == null ? '' : String(bv)).toLowerCase();
      return as.localeCompare(bs) * dir;
    });
  };

  // Stable options creator for header multi-select Autocompletes
  // (mirrors `projectFilterOptions` in the agent's `SuperPickerApp.tsx`).
  const multiSelectFilterOptions = useMemo(() => createFilterOptions<string>(), []);
  // Columns that should render a multi-select checkbox Autocomplete instead of
  // a free-text filter (project parity).
  const MULTI_SELECT_COLUMNS = useMemo(
    () => new Set(['projectName', 'projectCategory', 'district', 'region', 'nearestMrt', 'tenure']),
    [],
  );
  const computeOptions = <T,>(rows: T[], key: string): string[] => {
    const set = new Set<string>();
    for (const row of rows) {
      const v = (row as unknown as Record<string, unknown>)[key];
      if (v == null) continue;
      const s = String(v).trim();
      if (s) set.add(s);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  };
  const projectMultiOptions = useMemo<Record<string, string[]>>(() => {
    const out: Record<string, string[]> = {};
    MULTI_SELECT_COLUMNS.forEach((k) => { out[k] = computeOptions(projectRows, k); });
    return out;
  }, [projectRows, MULTI_SELECT_COLUMNS]);
  const unitMultiOptions = useMemo<Record<string, string[]>>(() => {
    const out: Record<string, string[]> = {};
    MULTI_SELECT_COLUMNS.forEach((k) => { out[k] = computeOptions(unitRows, k); });
    return out;
  }, [unitRows, MULTI_SELECT_COLUMNS]);

  // Decorate every project row with its Haversine distance against the
  // current Reference Location and drop projects beyond the chosen
  // radius. Runs only when `referenceCoords` / `radiusKmNumeric` change,
  // so flipping between Project/Unit views or tweaking other filters
  // doesn't recompute the geo math.
  const projectRowsWithDistance = useMemo<ProjectResearchRow[]>(() => {
    if (!referenceCoords) {
      // Strip any stale distance from a previous reference location.
      return projectRows.map((row) =>
        row.distanceFromReferenceKm == null
          ? row
          : { ...row, distanceFromReferenceKm: null },
      );
    }
    const decorated = projectRows.map((row) => {
      if (row.mapLatitude == null || row.mapLongitude == null) {
        return { ...row, distanceFromReferenceKm: null };
      }
      const km = haversineDistanceKm(
        referenceCoords.lat,
        referenceCoords.lng,
        row.mapLatitude,
        row.mapLongitude,
      );
      return { ...row, distanceFromReferenceKm: Math.round(km * 100) / 100 };
    });
    if (radiusKmNumeric == null) return decorated;
    return decorated.filter((row) =>
      row.distanceFromReferenceKm != null && row.distanceFromReferenceKm <= radiusKmNumeric,
    );
  }, [projectRows, referenceCoords, radiusKmNumeric]);

  // Same treatment for unit rows — distance is read off the unit's project
  // (looked up by `projectName` against the freshly-decorated map).
  const unitRowsWithDistance = useMemo<ProjectResearchUnitRow[]>(() => {
    if (!referenceCoords && unitRows.every((u) => u.distanceFromReferenceKm == null)) {
      return unitRows;
    }
    const projectByName = new Map(
      projectRowsWithDistance.map((p) => [p.projectName, p.distanceFromReferenceKm]),
    );
    const decorated = unitRows.map((row) => ({
      ...row,
      distanceFromReferenceKm: projectByName.get(row.projectName) ?? null,
    }));
    if (referenceCoords && radiusKmNumeric != null) {
      return decorated.filter((row) =>
        row.distanceFromReferenceKm != null && row.distanceFromReferenceKm <= radiusKmNumeric,
      );
    }
    return decorated;
  }, [unitRows, projectRowsWithDistance, referenceCoords, radiusKmNumeric]);

  const filteredProjects = useMemo(
    () => applySort(
      applyFilters(projectRowsWithDistance, PROJECT_COLUMNS, projectTextFilters, projectRangeFilters, projectMultiFilters),
      PROJECT_COLUMNS,
      projectSortConfig,
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectRowsWithDistance, projectTextFilters, projectRangeFilters, projectMultiFilters, projectSortConfig],
  );
  const filteredUnits = useMemo(
    () => applySort(
      applyFilters(unitRowsWithDistance, UNIT_COLUMNS, unitTextFilters, unitRangeFilters, unitMultiFilters),
      UNIT_COLUMNS,
      unitSortConfig,
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitRowsWithDistance, unitTextFilters, unitRangeFilters, unitMultiFilters, unitSortConfig],
  );

  // Reflect the *visible* filtered/radius-narrowed count rather than the
  // raw API totals — otherwise the "{n} projects found" hint and the view
  // toggle pills lie when the user types into a column filter or sets a
  // radius. `unitCount` falls back to the matched-units total when in
  // project view and no per-unit fetch has happened yet.
  const projectCount = filteredProjects.length;
  const unitCount = view === 'unit'
    ? filteredUnits.length
    : (unitRows.length > 0 ? filteredUnits.length : totalMatchedUnits);

  // Auto-sync the "selected for PDF" set with the currently filtered project rows
  // so the chip count reflects only the visible filtered subset (agent parity).
  useEffect(() => {
    if (view !== 'project') return;
    setSelectedIds(new Set(filteredProjects.map((p) => p.id)));
  }, [filteredProjects, view]);

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleIds = view === 'project'
    ? filteredProjects.map((p) => p.id)
    : filteredUnits.map((u) => u.id);

  const allSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allVisibleIds.forEach((id) => next.delete(id));
      } else {
        allVisibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const renderCaption = (label: string, required?: boolean) => (
    <Typography
      variant="caption"
      sx={{
        color: 'var(--color-text-strong)',
        fontWeight: 600,
        mb: 0.5,
        display: 'block',
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: 'var(--color-warning-strong)', ml: 0.25 }}>
          *
        </Box>
      )}
    </Typography>
  );

  const surfaceInputSx = {
    flex: 1,
    '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' },
  } as const;

  const renderNumberRange = (
    label: string,
    minKey: keyof ResearchFilters,
    maxKey: keyof ResearchFilters,
    opts?: { required?: boolean; thousands?: boolean },
  ) => {
    const sanitize = (v: string) => (opts?.thousands ? v.replace(/,/g, '').replace(/[^\d.]/g, '') : v);
    const display = (v: string) => (opts?.thousands ? formatThousandsInput(v) : v);
    return (
      <Box>
        {renderCaption(label, opts?.required)}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Min"
            autoComplete="off"
            inputProps={{ inputMode: opts?.thousands ? 'decimal' : 'numeric', autoComplete: 'off' }}
            value={display(filters[minKey] as string)}
            onChange={(e) => handleFilter(minKey, sanitize(e.target.value) as never)}
            sx={surfaceInputSx}
          />
          <TextField
            size="small"
            placeholder="Max"
            autoComplete="off"
            inputProps={{ inputMode: opts?.thousands ? 'decimal' : 'numeric', autoComplete: 'off' }}
            value={display(filters[maxKey] as string)}
            onChange={(e) => handleFilter(maxKey, sanitize(e.target.value) as never)}
            sx={surfaceInputSx}
          />
        </Box>
      </Box>
    );
  };

  const renderMultiSelect = (
    label: string,
    key: 'districts' | 'regions' | 'tenures' | 'categories',
    options: readonly string[],
    placeholder: string,
    formatOption?: (opt: string) => string,
  ) => (
    <Box>
      {renderCaption(label)}
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={options as readonly string[]}
        value={filters[key]}
        onChange={(_, value) => handleFilter(key, value as never)}
        getOptionLabel={(opt) => (formatOption ? formatOption(opt) : opt)}
        renderInput={(params) => (
          <TextField
            {...params}
            autoComplete="off"
            inputProps={{ ...params.inputProps, autoComplete: 'off' }}
            placeholder={placeholder}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((opt, idx) => (
            <Chip
              {...getTagProps({ index: idx })}
              key={opt}
              label={formatOption ? formatOption(opt) : opt}
              size="small"
            />
          ))
        }
      />
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicHeader />
      <Box sx={{ width: '100%', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        {/* Page header with back button */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <IconButton
            size="small"
            onClick={onBack}
            // Hidden (but kept in tree) for share-link recipients with no
            // listing to return to.
            sx={{ display: hideBackButton ? 'none' : undefined }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Project Research
          </Typography>
        </Box>

        {/* Search Criteria card */}
        <Card sx={{ mb: 3, ...mobileOptimizedStyles }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Search Criteria
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                {renderNumberRange('Price ($)', 'priceMin', 'priceMax', { required: true, thousands: true })}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderNumberRange('PSF ($/sqft)', 'psfMin', 'psfMax', { thousands: true })}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderNumberRange('Size (sqft)', 'sizeMin', 'sizeMax')}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderNumberRange('Bedroom', 'bedroomMin', 'bedroomMax')}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderNumberRange('Floor Level', 'floorMin', 'floorMax')}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderNumberRange('Bath', 'bathMin', 'bathMax')}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderMultiSelect(
                  'District',
                  'districts',
                  districtOpts.map((d) => d.no),
                  'Select districts',
                  (no) => {
                    const opt = districtOpts.find((d) => d.no === no);
                    const name = opt?.name ?? opt?.generalLocation ?? '';
                    return name ? `${no} — ${name}` : no;
                  },
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderMultiSelect('Region', 'regions', REGION_OPTIONS, 'Select regions')}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderMultiSelect('Tenure', 'tenures', TENURE_OPTIONS, 'Select tenure', formatTenureLabel)}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderMultiSelect(
                  'Project Category',
                  'categories',
                  categoryOpts.map((c) => c.name ?? c.id),
                  'Select categories',
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderNumberRange('TOP Year', 'topYearMin', 'topYearMax')}
              </Grid>

              {/* Reference Location (OneMap) — agent parity. Pure
                  client-side post-filter that narrows the project rows
                  by Haversine distance against the chosen radius. */}
              <Grid item xs={12} sm={6} md={6}>
                {renderCaption('Reference Project/Location')}
                <Autocomplete
                  size="small"
                  options={locationOptions}
                  value={selectedLocation}
                  inputValue={locationInput}
                  loading={locationSearching}
                  filterOptions={(x) => x}
                  getOptionLabel={(opt) => formatOnemapLocationLabel(opt)}
                  isOptionEqualToValue={(a, b) =>
                    a.SEARCHVAL === b.SEARCHVAL
                    && a.LATITUDE === b.LATITUDE
                    && a.LONGITUDE === b.LONGITUDE
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason !== 'input') return;
                    setLocationInput(value);
                    if (!value.trim()) {
                      clearLocationFilters();
                      return;
                    }
                    // If the user keeps typing after picking a location,
                    // drop the prior selection so the radius doesn't
                    // silently apply to a stale anchor point.
                    if (
                      selectedLocation
                      && value !== formatOnemapLocationLabel(selectedLocation)
                    ) {
                      setSelectedLocation(null);
                      setRadiusKm('');
                    }
                  }}
                  onChange={(_, value) => {
                    if (!value) {
                      clearLocationFilters();
                      return;
                    }
                    const coords = parseOnemapCoordinates(value);
                    if (!coords) return;
                    setSelectedLocation(value);
                    setLocationInput(formatOnemapLocationLabel(value));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoComplete="off"
                      inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                      placeholder="Search address or place"
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--color-surface)' } }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {locationSearching ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderCaption('Radius (km)')}
                <TextField
                  size="small"
                  type="text"
                  disabled={!selectedLocation}
                  autoComplete="off"
                  inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
                  placeholder="e.g. 5"
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(e.target.value.replace(/[^\d.]/g, ''))}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'var(--color-surface)',
                      // Highlight in accent when the radius is missing
                      // for a chosen location — same affordance the agent
                      // uses to nudge the user before clicking SEARCH.
                      ...(isRadiusMissing
                        ? {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--color-accent)',
                            borderWidth: 2,
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--color-accent)',
                          },
                        }
                        : {}),
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Typography variant="body2" sx={{ mt: 2, color: 'var(--color-warning-strong)', fontWeight: 600 }}>
              * Note: Normal Payment Scheme (NPS) price will be used as price search for Executive Condo
            </Typography>

            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading || isRadiusMissing}
                sx={{
                  bgcolor: 'var(--color-accent)',
                  '&:hover': { bgcolor: 'var(--color-accent-hover)' },
                  fontWeight: 700,
                }}
              >
                {loading ? 'SEARCHING…' : 'SEARCH'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClear}
                sx={{
                  borderColor: 'var(--color-accent)',
                  color: 'var(--color-accent)',
                  fontWeight: 700,
                }}
              >
                CLEAR
              </Button>
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', ml: 'auto' }}>
                {view === 'project' ? `${projectCount} projects found` : `${unitCount} units found`}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Results card — hidden until the user actually triggers a search
            (agent parity with `SuperPickerApp` Master Tool results gate). */}
        {hasSearched && (
        <Card sx={{ mb: 3, ...mobileOptimizedStyles }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                mb: 2,
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {/* Left column: title + NPS + Export PDF */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: 'var(--color-text-strong)' }}
                >
                  Results
                </Typography>
                {view === 'project' && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'var(--color-warning-strong)', fontWeight: 600 }}
                  >
                    * Note: Normal Payment Scheme (NPS) price will be used as price search for Executive Condo
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon fontSize="small" />}
                    disabled={
                      view === 'project'
                        ? selectedIds.size === 0
                        : unitCount === 0
                    }
                  >
                    Export PDF
                  </Button>
                  {view === 'project' && selectedIds.size > 0 && (
                    <Chip
                      label={`${selectedIds.size} selected for PDF`}
                      size="small"
                      onDelete={() => setSelectedIds(new Set())}
                      sx={{
                        bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.15)',
                        color: 'var(--color-accent)',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Right column: view toggle + columns */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={view}
                  onChange={(_, v) => v && setView(v)}
                >
                  <ToggleButton
                    value="project"
                    sx={{
                      fontSize: '0.7rem',
                      py: 0.5,
                      color: 'var(--color-text-strong)',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.15)',
                        color: 'var(--color-accent)',
                      },
                    }}
                  >
                    Project View ({projectCount})
                  </ToggleButton>
                  <ToggleButton
                    value="unit"
                    sx={{
                      fontSize: '0.7rem',
                      py: 0.5,
                      color: 'var(--color-text-strong)',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.15)',
                        color: 'var(--color-accent)',
                      },
                    }}
                  >
                    Unit View ({unitCount})
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ViewColumnIcon />}
                  onClick={(e) => setColumnsAnchor(e.currentTarget)}
                >
                  Columns
                </Button>
              </Box>
            </Box>

            {loading && (
              <LinearProgress
                sx={{ mb: 2, '& .MuiLinearProgress-bar': { bgcolor: 'var(--color-accent)' } }}
              />
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {!loading && !error && hasSearched && projectRows.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No projects matched your search criteria.
              </Alert>
            )}

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  '& .MuiTableCell-root': {
                    py: 0.75,
                    px: 1,
                    fontSize: '0.8rem',
                    borderColor: 'rgba(var(--color-text-rgb), 0.08)',
                    color: 'var(--color-text-strong)',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    bgcolor: 'var(--color-surface-thead, var(--color-surface))',
                    fontWeight: 600,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* Selection column is project-view only — unit rows are not selectable. */}
                    {view === 'project' && (
                      <TableCell
                        padding="checkbox"
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          minWidth: 48,
                          width: 48,
                          bgcolor: 'var(--color-surface-thead, var(--color-surface))',
                          borderRight: '1px solid rgba(var(--color-text-rgb), 0.1)',
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={!allSelected && allVisibleIds.some((id) => selectedIds.has(id))}
                          onChange={toggleSelectAll}
                          sx={{
                            color: 'var(--color-accent)',
                            '&.Mui-checked': { color: 'var(--color-accent)' },
                            '&.MuiCheckbox-indeterminate': { color: 'var(--color-accent)' },
                          }}
                        />
                      </TableCell>
                    )}
                    {view === 'project'
                      ? PROJECT_COLUMNS.filter((c) => visibleProjectCols.has(c.key)).map((c) => {
                          const meta = c as { numeric?: boolean; width?: number };
                          const isNumeric = !!meta.numeric;
                          return (
                            <TableCell
                              key={c.key}
                              align={isNumeric ? 'right' : 'left'}
                              data-column={c.key}
                              sx={{
                                whiteSpace: 'nowrap',
                                fontWeight: 700,
                                minWidth: meta.width,
                                width: meta.width,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: isNumeric ? 'flex-end' : 'flex-start',
                                  gap: 0.5,
                                  width: '100%',
                                }}
                              >
                                {c.label}
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setColumnPopoverAnchor({
                                      element: e.currentTarget,
                                      column: c.key,
                                      view: 'project',
                                    });
                                  }}
                                  sx={getColumnIconButtonSx('project', c.key)}
                                >
                                  {renderSortIndicatorIcon(getColumnSortDirection('project', c.key))}
                                </IconButton>
                              </Box>
                            </TableCell>
                          );
                        })
                      : UNIT_COLUMNS.filter((c) => visibleUnitCols.has(c.key)).map((c) => {
                          const meta = c as { numeric?: boolean; width?: number };
                          const isNumeric = !!meta.numeric;
                          return (
                            <TableCell
                              key={c.key}
                              align={isNumeric ? 'right' : 'left'}
                              data-column={c.key}
                              sx={{
                                whiteSpace: 'nowrap',
                                fontWeight: 700,
                                minWidth: meta.width,
                                width: meta.width,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: isNumeric ? 'flex-end' : 'flex-start',
                                  gap: 0.5,
                                  width: '100%',
                                }}
                              >
                                {c.label}
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setColumnPopoverAnchor({
                                      element: e.currentTarget,
                                      column: c.key,
                                      view: 'unit',
                                    });
                                  }}
                                  sx={getColumnIconButtonSx('unit', c.key)}
                                >
                                  {renderSortIndicatorIcon(getColumnSortDirection('unit', c.key))}
                                </IconButton>
                              </Box>
                            </TableCell>
                          );
                        })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {view === 'project'
                    ? filteredProjects.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((row) => {
                        const visibleProjectColumns = PROJECT_COLUMNS.filter((c) =>
                          visibleProjectCols.has(c.key),
                        );
                        const expandColSpan = visibleProjectColumns.length + 1; // +1 for checkbox col
                        const isRowExpanded = matchedExpand?.projectId === row.id;
                        const matchedCountBuckets: Record<MatchedExpandBucket, number> = {
                          total: row.matched,
                          br1: row.br1,
                          br2: row.br2,
                          br3: row.br3,
                          br4: row.br4,
                          br5: row.br5,
                          ph: row.ph,
                        };
                        return (
                        <Fragment key={row.id}>
                        <TableRow hover>
                          <TableCell
                            padding="checkbox"
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              bgcolor: 'var(--color-surface)',
                              borderRight: '1px solid rgba(var(--color-text-rgb), 0.1)',
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={selectedIds.has(row.id)}
                              onChange={() => toggleSelected(row.id)}
                              sx={{
                                color: 'var(--color-accent)',
                                '&.Mui-checked': { color: 'var(--color-accent)' },
                              }}
                            />
                          </TableCell>
                          {visibleProjectColumns.map((c) => {
                            const value = row[c.key as keyof ProjectResearchRow];
                            const meta = c as { numeric?: boolean; money?: boolean; width?: number };
                            const isNumeric = meta.numeric;
                            const isMoney = meta.money;
                            const accent =
                              isNumeric &&
                              ['matched', 'br1', 'br2', 'br3', 'br4', 'br5', 'ph'].includes(c.key) &&
                              Number(value) > 0;

                            // Project Name cell renders the name plus a Tower View
                            // launcher icon underneath — agent parity with
                            // `handleOpenTowerView` in `SuperPickerApp.tsx`.
                            if (c.key === 'projectName') {
                              return (
                                <TableCell
                                  key={c.key}
                                  align="left"
                                  sx={{
                                    whiteSpace: 'normal',
                                    minWidth: meta.width,
                                    width: meta.width,
                                    fontWeight: 500,
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: 'var(--color-text-strong)', whiteSpace: 'normal' }}
                                    >
                                      {row.projectName || '-'}
                                    </Typography>
                                    <Box>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenTowerView(
                                            row.id,
                                            row.projectName || '',
                                            projectFilterBedrooms.length > 0
                                              ? { kind: 'bedrooms', bedrooms: projectFilterBedrooms }
                                              : null,
                                          );
                                        }}
                                        title="View Tower View"
                                        sx={{
                                          p: 0.5,
                                          color: 'var(--color-text-strong)',
                                          '&:hover': {
                                            bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.1)',
                                            color: 'var(--color-accent)',
                                          },
                                        }}
                                      >
                                        <ViewModuleIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                </TableCell>
                              );
                            }

                            // Matched / per-bedroom / PH cells become clickable
                            // expanders that reveal a sub-table of matched units
                            // (agent parity with `renderMatchedExpandCell`).
                            const expandBucketByKey: Record<string, MatchedExpandBucket> = {
                              matched: 'total',
                              br1: 'br1', br2: 'br2', br3: 'br3', br4: 'br4', br5: 'br5', ph: 'ph',
                            };
                            const expandBucket = expandBucketByKey[c.key];
                            if (expandBucket) {
                              const n = Number(value) || 0;
                              const isCellExpanded =
                                matchedExpand?.projectId === row.id &&
                                matchedExpand?.bucket === expandBucket;
                              return (
                                <TableCell
                                  key={c.key}
                                  align="center"
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    minWidth: meta.width,
                                    width: meta.width,
                                  }}
                                >
                                  {n > 0 ? (
                                    <Box
                                      component="button"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMatchedExpandClick(row, expandBucket, n);
                                      }}
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.25,
                                        justifyContent: 'center',
                                        m: 0,
                                        p: 0,
                                        minWidth: 0,
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        color: 'var(--color-accent)',
                                        fontWeight: 600,
                                        font: 'inherit',
                                        textDecoration: 'underline dotted rgba(255, 152, 0, 0.55)',
                                        textUnderlineOffset: 3,
                                        '&:hover': { opacity: 0.92 },
                                      }}
                                    >
                                      {n}
                                      {isCellExpanded ? (
                                        <KeyboardArrowUpIcon sx={{ fontSize: '1rem' }} />
                                      ) : (
                                        <KeyboardArrowDownIcon sx={{ fontSize: '1rem' }} />
                                      )}
                                    </Box>
                                  ) : '-'}
                                </TableCell>
                              );
                            }

                            // Money columns receive a backend-masked string
                            // (or null when no price is available) — render as
                            // `$...` or `-`. Pure numeric columns keep their 0
                            // so empty bedroom buckets still show "0".
                            let display: React.ReactNode;
                            if (c.key === 'distanceFromReferenceKm') {
                              display = formatReferenceDistanceKm(value);
                            } else if (isMoney) {
                              display = formatPrice(
                                typeof value === 'string' ? value : null,
                              );
                            } else if (isNumeric) {
                              display = value == null || value === '' ? '-' : String(value);
                            } else {
                              display = value == null || value === '' ? '-' : String(value);
                            }
                            return (
                              <TableCell
                                key={c.key}
                                align={isNumeric ? 'right' : 'left'}
                                sx={{
                                  whiteSpace: 'nowrap',
                                  minWidth: meta.width,
                                  width: meta.width,
                                  color: accent ? 'var(--color-accent)' : undefined,
                                  fontWeight: accent ? 600 : undefined,
                                }}
                              >
                                {display}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        {isRowExpanded && (
                          <TableRow>
                            <TableCell
                              colSpan={expandColSpan}
                              sx={{
                                p: 0,
                                borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
                                bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                              }}
                            >
                              <Collapse in timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      mb: 1,
                                      color: 'rgba(var(--color-text-rgb), 0.65)',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {matchedExpand?.bucket === 'total'
                                      ? 'Matched units (all)'
                                      : matchedExpand?.bucket === 'ph'
                                      ? 'Matched units (PH)'
                                      : `Matched units (${(matchedExpand?.bucket ?? '').replace('br', '')}BR)`}
                                  </Typography>
                                  {matchedUnitsLoading[row.id] && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                      <CircularProgress size={28} />
                                    </Box>
                                  )}
                                  {matchedUnitsError[row.id] && (
                                    <Alert severity="error" sx={{ mb: 1 }}>
                                      {matchedUnitsError[row.id]}
                                    </Alert>
                                  )}
                                  {(() => {
                                    if (matchedUnitsLoading[row.id] || matchedUnitsError[row.id]) return null;
                                    const cached = matchedUnitsByProject[row.id] ?? [];
                                    const filtered = matchedExpand
                                      ? cached.filter((u) => unitMatchesBucket(u, matchedExpand.bucket))
                                      : cached;
                                    if (filtered.length === 0) {
                                      return (
                                        <Typography
                                          variant="body2"
                                          sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}
                                        >
                                          No units in this group.
                                        </Typography>
                                      );
                                    }
                                    return (
                                      <Box sx={{ overflowX: 'auto' }}>
                                        <Table
                                          size="small"
                                          sx={{
                                            minWidth: 1900,
                                            tableLayout: 'fixed',
                                            '& .MuiTableCell-root': {
                                              py: 0.75,
                                              px: 1,
                                              fontSize: '0.8rem',
                                              borderColor: 'rgba(var(--color-text-rgb), 0.08)',
                                              color: 'var(--color-text-strong)',
                                            },
                                            '& .MuiTableHead-root .MuiTableCell-root': {
                                              bgcolor: 'var(--color-surface-thead)',
                                              fontWeight: 600,
                                            },
                                          }}
                                        >
                                          <TableHead>
                                            <TableRow>
                                              {([
                                                ['Project Name', 200],
                                                ['Project Category', 140],
                                                ['Unit', 100],
                                                ['Block', 90],
                                                ['Floor', 70],
                                                ['Stack', 70],
                                                ['Bed', 60],
                                                ['Bath', 60],
                                                ['Area (sqft)', 110],
                                                ['Price/NPS $', 130],
                                                ['DPS $', 110],
                                                ['PSF $', 100],
                                                ['District', 90],
                                                ['District/Location', 170],
                                                ['Region', 90],
                                                ['Nearest MRT', 150],
                                                ['Walking Time', 110],
                                                ['Tenure', 130],
                                                ['TOP Year', 90],
                                              ] as Array<[string, number]>).map(([label, width]) => (
                                                <TableCell
                                                  key={label}
                                                  sx={{
                                                    fontWeight: 600,
                                                    bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
                                                    whiteSpace: 'nowrap',
                                                    minWidth: width,
                                                    width,
                                                  }}
                                                >
                                                  {label}
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {filtered.map((unit) => {
                                              const menuUnit: PublicUnitForMenu = {
                                                unitKey: String(unit.id),
                                                projectId: unit.projectId ?? null,
                                                projectName: unit.projectName,
                                                unitNumber: unit.unitNumber,
                                                blockNumber: unit.blockNumber,
                                                bedroom: unit.bedroom,
                                                bathroom: unit.bathroom,
                                                areaSqft: unit.areaSqft,
                                                unitStatus: null,
                                                floorplan: unit.floorplan,
                                                priceDisplay: unit.price ? formatPrice(unit.price) : null,
                                                psfDisplay: unit.psf ? formatPrice(unit.psf) : null,
                                              };
                                              return (
                                                <TableRow
                                                  key={`expand-${row.id}-${unit.id}`}
                                                  hover
                                                  onClick={(e) => {
                                                    if ((e.target as HTMLElement).closest('button')) return;
                                                    handleOpenUnitMenu(menuUnit, e);
                                                  }}
                                                  sx={{ cursor: 'pointer' }}
                                                >
                                                  <TableCell sx={{ fontWeight: 500, minWidth: 200 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                      <Typography variant="body2" sx={{ color: 'var(--color-text-strong)' }}>
                                                        {unit.projectName}
                                                      </Typography>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <IconButton
                                                          size="small"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenTowerView(
                                                              row.id,
                                                              row.projectName || '',
                                                              unit.computedStack != null
                                                                ? { kind: 'stacks', stacks: [String(unit.computedStack)] }
                                                                : null,
                                                            );
                                                          }}
                                                          title="View Tower View"
                                                          sx={{
                                                            p: 0.5,
                                                            color: 'var(--color-text-strong)',
                                                            '&:hover': {
                                                              bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.1)',
                                                              color: 'var(--color-accent)',
                                                            },
                                                          }}
                                                        >
                                                          <ViewModuleIcon fontSize="small" />
                                                        </IconButton>
                                                        {unit.floorplan && (
                                                          <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setFloorPlanUnit(menuUnit);
                                                            }}
                                                            title="View Floor Plan"
                                                            sx={{
                                                              p: 0.5,
                                                              color: 'var(--color-accent)',
                                                              '&:hover': {
                                                                bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.1)',
                                                              },
                                                            }}
                                                          >
                                                            <ImageIcon fontSize="small" />
                                                          </IconButton>
                                                        )}
                                                      </Box>
                                                    </Box>
                                                  </TableCell>
                                                  <TableCell>{row.projectCategory || '-'}</TableCell>
                                                  <TableCell>{unit.unitNumber || '-'}</TableCell>
                                                  <TableCell align="center">{unit.blockNumber || '-'}</TableCell>
                                                  <TableCell align="center">{unit.computedFloor ?? '-'}</TableCell>
                                                  <TableCell align="center">{unit.computedStack ?? '-'}</TableCell>
                                                  <TableCell align="center">{unit.bedroom}</TableCell>
                                                  <TableCell align="center">{unit.bathroom}</TableCell>
                                                  <TableCell align="right">{unit.areaSqft > 0 ? unit.areaSqft.toLocaleString('en-SG') : '-'}</TableCell>
                                                  <TableCell align="right">{formatPrice(unit.price)}</TableCell>
                                                  <TableCell align="right">-</TableCell>
                                                  <TableCell align="right">{formatPrice(unit.psf)}</TableCell>
                                                  <TableCell>{row.district || '-'}</TableCell>
                                                  <TableCell>{row.districtLocation || '-'}</TableCell>
                                                  <TableCell>{row.region || '-'}</TableCell>
                                                  <TableCell>{row.nearestMrt || '-'}</TableCell>
                                                  <TableCell align="right">{row.walkingTimeToMrt || '-'}</TableCell>
                                                  <TableCell>{row.tenure || '-'}</TableCell>
                                                  <TableCell align="right">{row.topDate || '-'}</TableCell>
                                                </TableRow>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    );
                                  })()}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                        </Fragment>
                        );
                      })
                    : filteredUnits.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((row) => {
                        const rowMenuUnit: PublicUnitForMenu = {
                          unitKey: row.id,
                          projectId: row.projectId ?? null,
                          projectName: row.projectName,
                          unitNumber: row.unitNumber,
                          blockNumber: row.blockNumber,
                          bedroom: row.bedroom,
                          bathroom: row.bathroom,
                          areaSqft: row.areaSqft,
                          unitStatus: null,
                          floorplan: row.floorplan,
                          priceDisplay: row.price ? formatPrice(row.price) : null,
                          psfDisplay: row.psf ? formatPrice(row.psf) : null,
                        };
                        return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('button')) return;
                            handleOpenUnitMenu(rowMenuUnit, e);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          {/* Unit rows are not selectable — no PDF export from the unit view. */}
                          {UNIT_COLUMNS.filter((c) => visibleUnitCols.has(c.key)).map((c) => {
                            const value = row[c.key as keyof ProjectResearchUnitRow];
                            const meta = c as { numeric?: boolean; money?: boolean; width?: number };
                            const isNumeric = meta.numeric;
                            const isMoney = meta.money;
                            // Look up unmasked prices for this row (if the
                            // recipient has paid to reveal it). Used below
                            // in the money-cell branch to swap the masked
                            // string for the real numeric value.
                            const unitRevealed = row.projectId
                              ? revealedStore.towerView[row.projectId]?.[
                                  buildPublicUnitSyntheticKey(row.blockNumber, row.unitNumber)
                                ]
                              : undefined;

                            // Project Name cell: render the name plus the same
                            // Tower View + Floor Plan icon row used in the project
                            // view and the matched-units expansion (agent parity).
                            if (c.key === 'projectName') {
                              const menuUnit = rowMenuUnit;
                              return (
                                <TableCell
                                  key={c.key}
                                  align="left"
                                  sx={{
                                    whiteSpace: 'normal',
                                    minWidth: meta.width,
                                    width: meta.width,
                                    fontWeight: 500,
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: 'var(--color-text-strong)', whiteSpace: 'normal' }}
                                    >
                                      {row.projectName || '-'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenTowerView(
                                            row.projectId,
                                            row.projectName || '',
                                            row.stack !== '' && row.stack != null
                                              ? { kind: 'stacks', stacks: [String(row.stack)] }
                                              : null,
                                          );
                                        }}
                                        title="View Tower View"
                                        sx={{
                                          p: 0.5,
                                          color: 'var(--color-text-strong)',
                                          '&:hover': {
                                            bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.1)',
                                            color: 'var(--color-accent)',
                                          },
                                        }}
                                      >
                                        <ViewModuleIcon fontSize="small" />
                                      </IconButton>
                                      {row.floorplan && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setFloorPlanUnit(menuUnit);
                                          }}
                                          title="View Floor Plan"
                                          sx={{
                                            p: 0.5,
                                            color: 'var(--color-accent)',
                                            '&:hover': {
                                              bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.1)',
                                            },
                                          }}
                                        >
                                          <ImageIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                              );
                            }

                            let display: React.ReactNode;
                            if (c.key === 'distanceFromReferenceKm') {
                              display = formatReferenceDistanceKm(value);
                            } else if (isMoney) {
                              const revealedNum =
                                unitRevealed && c.key === 'price'
                                  ? unitRevealed.price
                                  : unitRevealed && c.key === 'priceDps'
                                    ? unitRevealed.priceDps
                                    : unitRevealed && c.key === 'psf'
                                      ? unitRevealed.psf
                                      : null;
                              if (
                                revealedNum != null &&
                                Number.isFinite(revealedNum) &&
                                revealedNum > 0
                              ) {
                                display = `$${Math.round(revealedNum).toLocaleString('en-SG')}`;
                              } else {
                                display = formatPrice(
                                  typeof value === 'string' ? value : null,
                                );
                              }
                            } else if (isNumeric) {
                              display = value == null || value === '' ? '-' : String(value);
                            } else {
                              display = value == null || value === '' ? '-' : String(value);
                            }
                            return (
                              <TableCell
                                key={c.key}
                                align={isNumeric ? 'right' : 'left'}
                                sx={{
                                  whiteSpace: 'nowrap',
                                  minWidth: meta.width,
                                  width: meta.width,
                                }}
                              >
                                {display}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={view === 'project' ? filteredProjects.length : filteredUnits.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
              onRowsPerPageChange={() => undefined}
            />
          </CardContent>
        </Card>
        )}

        {/* Per-column Sort + Filter popover (agent parity with `masterToolPopoverAnchor`). */}
        <Popover
          open={Boolean(columnPopoverAnchor)}
          anchorEl={columnPopoverAnchor?.element}
          onClose={closeColumnPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {columnPopoverAnchor && (() => {
            const { column, view: popoverView } = columnPopoverAnchor;
            const columns: ReadonlyArray<{ key: string; label: string; numeric?: boolean; money?: boolean }> =
              popoverView === 'project' ? PROJECT_COLUMNS : UNIT_COLUMNS;
            const colMeta = columns.find((c) => c.key === column);
            const label = colMeta?.label ?? column;
            const isNumeric = !!colMeta?.numeric;
            const shouldFormatThousands = !!colMeta?.money || /(price|psf)$/i.test(column);
            const sortDirection = getColumnSortDirection(popoverView, column);
            const range =
              (popoverView === 'project' ? projectRangeFilters : unitRangeFilters)[column] ?? { min: '', max: '' };
            const text = (popoverView === 'project' ? projectTextFilters : unitTextFilters)[column] ?? '';
            const setRange = (next: RangeValue) => {
              if (popoverView === 'project') {
                setProjectRangeFilters((prev) => ({ ...prev, [column]: next }));
              } else {
                setUnitRangeFilters((prev) => ({ ...prev, [column]: next }));
              }
              setPage(0);
            };
            const setText = (next: string) => {
              if (popoverView === 'project') {
                setProjectTextFilters((prev) => ({ ...prev, [column]: next }));
              } else {
                setUnitTextFilters((prev) => ({ ...prev, [column]: next }));
              }
              setPage(0);
            };
            const multiValues =
              (popoverView === 'project' ? projectMultiFilters : unitMultiFilters)[column] ?? [];
            const setMulti = (next: string[]) => {
              if (popoverView === 'project') {
                setProjectMultiFilters((prev) => ({ ...prev, [column]: next }));
              } else {
                setUnitMultiFilters((prev) => ({ ...prev, [column]: next }));
              }
              setPage(0);
            };
            const isMultiSelectColumn = MULTI_SELECT_COLUMNS.has(column);
            const multiOptionsForColumn =
              (popoverView === 'project' ? projectMultiOptions : unitMultiOptions)[column] ?? [];
            const multiSelectInputLabel = `Select ${label}`;
            const multiSelectAllLabel =
              column === 'projectName'
                ? 'All Projects'
                : `All ${label}`;
            const clearFilter = () => {
              if (isMultiSelectColumn) setMulti([]);
              else if (isNumeric) setRange({ min: '', max: '' });
              else setText('');
            };
            const applySortChange = (direction: SortDirection) => {
              if (popoverView === 'project') {
                setProjectSortConfig({ column: column as ProjectColumnKey, direction });
              } else {
                setUnitSortConfig({ column: column as UnitColumnKey, direction });
              }
              setPage(0);
            };
            return (
              <Box sx={{ p: 2, minWidth: 280 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, color: 'var(--color-info, var(--color-accent))', fontWeight: 500 }}
                  >
                    Sort
                  </Typography>
                  <ToggleButtonGroup
                    value={sortDirection}
                    exclusive
                    onChange={(_, value) => {
                      if (value !== null) applySortChange(value as SortDirection);
                    }}
                    fullWidth
                    sx={{
                      '& .MuiToggleButtonGroup-grouped': {
                        border: '1px solid rgba(var(--color-shadow-rgb, 0,0,0), 0.12)',
                        '&:not(:first-of-type)': {
                          borderLeft: '1px solid rgba(var(--color-shadow-rgb, 0,0,0), 0.12)',
                          marginLeft: 0,
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'var(--color-info, var(--color-accent))',
                          color: 'var(--color-text-strong)',
                        },
                      },
                    }}
                  >
                    <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>Default</ToggleButton>
                    <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>Asc</ToggleButton>
                    <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>Desc</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'var(--color-info, var(--color-accent))', fontWeight: 500, mr: 1 }}
                    >
                      Filter
                    </Typography>
                    <FilterListIcon
                      sx={{ fontSize: 18, color: 'var(--color-info, var(--color-accent))' }}
                    />
                  </Box>
                  {isMultiSelectColumn ? (
                    <Box>
                      <Autocomplete
                        multiple
                        fullWidth
                        size="small"
                        disableCloseOnSelect
                        options={
                          multiOptionsForColumn.length > 0
                            ? ['__SELECT_ALL__', ...multiOptionsForColumn]
                            : multiOptionsForColumn
                        }
                        value={multiValues}
                        filterOptions={(options, state) => {
                          const filtered = multiSelectFilterOptions(
                            options.filter((option) => option !== '__SELECT_ALL__'),
                            state,
                          );
                          return multiOptionsForColumn.length > 0
                            ? ['__SELECT_ALL__', ...filtered]
                            : filtered;
                        }}
                        onChange={(_, value) => {
                          const hasSelectAll = value.includes('__SELECT_ALL__');
                          if (hasSelectAll) {
                            if (multiValues.length === multiOptionsForColumn.length) {
                              setMulti([]);
                            } else {
                              setMulti([...multiOptionsForColumn]);
                            }
                            return;
                          }
                          setMulti(value as string[]);
                        }}
                        renderOption={(props, option) => {
                          if (option === '__SELECT_ALL__') {
                            const allSelected =
                              multiValues.length === multiOptionsForColumn.length &&
                              multiOptionsForColumn.length > 0;
                            const indeterminate =
                              multiValues.length > 0 &&
                              multiValues.length < multiOptionsForColumn.length;
                            return (
                              <li {...props}>
                                <Checkbox
                                  checked={allSelected}
                                  indeterminate={indeterminate}
                                  sx={{
                                    color: 'var(--color-accent)',
                                    '&.Mui-checked': { color: 'var(--color-accent)' },
                                    '&.MuiCheckbox-indeterminate': { color: 'var(--color-accent)' },
                                  }}
                                />
                                <Typography component="span" sx={{ fontWeight: 600 }}>
                                  Select All
                                </Typography>
                              </li>
                            );
                          }
                          const isSelected = multiValues.includes(option);
                          return (
                            <li {...props}>
                              <Checkbox
                                checked={isSelected}
                                sx={{
                                  color: 'var(--color-accent)',
                                  '&.Mui-checked': { color: 'var(--color-accent)' },
                                }}
                              />
                              {option}
                            </li>
                          );
                        }}
                        renderTags={(value, getTagProps) => {
                          const summary =
                            value.length === 0
                              ? multiSelectAllLabel
                              : value.length === multiOptionsForColumn.length
                              ? 'All Selected'
                              : `${value.length} selected`;
                          return <Chip label={summary} size="small" {...getTagProps({ index: 0 })} />;
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={multiSelectInputLabel}
                            placeholder="Search..."
                          />
                        )}
                      />
                      <Button
                        size="small"
                        onClick={clearFilter}
                        sx={{ mt: 1 }}
                        fullWidth
                        disabled={!hasActiveColumnFilter(popoverView, column)}
                      >
                        Clear Filter
                      </Button>
                    </Box>
                  ) : isNumeric ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type={shouldFormatThousands ? 'text' : 'number'}
                        inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                        label="Min"
                        value={shouldFormatThousands ? formatThousandsInput(range.min ?? '') : (range.min ?? '')}
                        onChange={(e) => {
                          const nextValue = shouldFormatThousands
                            ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '')
                            : e.target.value;
                          setRange({ ...range, min: nextValue });
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type={shouldFormatThousands ? 'text' : 'number'}
                        inputProps={shouldFormatThousands ? { inputMode: 'decimal' } : undefined}
                        label="Max"
                        value={shouldFormatThousands ? formatThousandsInput(range.max ?? '') : (range.max ?? '')}
                        onChange={(e) => {
                          const nextValue = shouldFormatThousands
                            ? e.target.value.replace(/,/g, '').replace(/[^\d.]/g, '')
                            : e.target.value;
                          setRange({ ...range, max: nextValue });
                        }}
                      />
                      <Button
                        size="small"
                        onClick={clearFilter}
                        fullWidth
                        disabled={!hasActiveColumnFilter(popoverView, column)}
                      >
                        Clear Filter
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={`Filter ${label}`}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        InputProps={{
                          endAdornment: text ? (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setText('')} sx={{ p: 0.5 }}>
                                <Typography variant="caption" sx={{ fontSize: 12 }}>✕</Typography>
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                      <Button
                        size="small"
                        onClick={clearFilter}
                        sx={{ mt: 1 }}
                        fullWidth
                        disabled={!hasActiveColumnFilter(popoverView, column)}
                      >
                        Clear Filter
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })()}
        </Popover>

        {/* Tower View dialog — opened from the grid icon under each project name
            (agent parity with `handleOpenTowerView` in `SuperPickerApp.tsx`). */}
        <Dialog
          open={Boolean(towerViewDialog)}
          onClose={closeTowerView}
          maxWidth={false}
          fullWidth
          PaperProps={{ sx: { width: '95vw', maxWidth: '1600px' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              pr: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ViewModuleIcon sx={{ color: 'var(--color-accent)' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Tower View{towerViewDialog?.projectName ? ` — ${towerViewDialog.projectName}` : ''}
              </Typography>
            </Box>
            <IconButton size="small" onClick={closeTowerView}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ minHeight: 320 }}>
            {towerViewLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={32} />
              </Box>
            )}
            {!towerViewLoading && towerViewError && (
              <Alert severity="error">{towerViewError}</Alert>
            )}
            {!towerViewLoading && !towerViewError && towerViewUnits.length === 0 && towerViewDialog && (
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                No tower view data available for this project.
              </Typography>
            )}
            {!towerViewLoading && !towerViewError && towerViewUnits.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <SitePlanSectionDetail
                  projectName={towerViewDialog?.projectName ?? ''}
                  projectId={towerViewDialog?.projectId ?? null}
                  onUnitClick={handleUnitClick}
                  imageUrl={towerViewSitePlanImageUrl}
                  blocks={towerViewSitePlanBlocks}
                  bounds={towerViewSitePlanBounds}
                  stackInfos={towerViewStackInfos}
                  stackSoldPercentages={towerViewStackSold}
                  blinkingStacks={towerViewBlinkingStacks}
                  mapLatitude={towerViewDetails?.mapLatitude ?? null}
                  mapLongitude={towerViewDetails?.mapLongitude ?? null}
                  bedroomFilter={towerViewBedroomFilter}
                  onBedroomFilterChange={setTowerViewBedroomFilter}
                  unitTypeFilter={towerViewUnitTypeFilter}
                  onUnitTypeFilterChange={setTowerViewUnitTypeFilter}
                  stackFilter={towerViewStackFilter}
                />
                <TowerViewSection
                  units={towerViewUnits}
                  projectId={towerViewDialog?.projectId ?? null}
                  onCellClick={handleUnitClick}
                  isInCompareCart={(unitKey) =>
                    compare.isInCart(unitKey, towerViewDialog?.projectId ?? null)
                  }
                  bedroomFilter={towerViewBedroomFilter}
                  unitTypeFilter={towerViewUnitTypeFilter}
                  stackFilter={towerViewStackFilter}
                  onStackFilterChange={setTowerViewStackFilter}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeTowerView}>Close</Button>
          </DialogActions>
        </Dialog>

        <UnitActionsMenu
          anchor={unitMenuAnchor}
          onClose={() => setUnitMenuAnchor(null)}
          onViewFloorPlan={handleViewFloorPlanFromMenu}
          onToggleCompare={handleToggleCompare}
          onRevealPrices={
            canRevealPrices && unitMenuAnchor?.unit.projectId
              ? handleRevealPricesRequest
              : undefined
          }
          isPricesRevealed={isUnitRevealed(unitMenuAnchor?.unit)}
          isInCart={(unitKey, projectId) => compare.isInCart(unitKey, projectId)}
          cartSize={compare.cart.length}
          capacity={compare.capacity}
        />
        {/* Token-spend confirmation dialog for the per-row Reveal Prices flow. */}
        {revealPrices.dialog}
        <FloorPlanModal
          open={Boolean(floorPlanUnit)}
          unit={floorPlanUnit}
          onClose={() => setFloorPlanUnit(null)}
          isInCompare={
            floorPlanUnit
              ? compare.isInCart(floorPlanUnit.unitKey, floorPlanUnit.projectId)
              : false
          }
          compareCount={compare.cart.length}
          compareCapacity={compare.capacity}
          onToggleCompare={handleToggleCompare}
          onOpenCompare={() => {
            logPublicFloorPlanComparisonOpened({ source: 'floor_plan_modal' });
            compare.openCompare();
          }}
        />

        {/* Token-spend confirmation prompt for the Search button. Owns its
            own open/close state; clicking confirm calls the backend
            `consume` endpoint, pushes the updated counters into
            `shareContext`, then runs the actual search. */}
        {tokenSpend.dialog}

        <Footer />
      </Box>

      {/* Columns menu */}
      <Menu
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
      >
        <Box sx={{ minWidth: 220, px: 1.5, py: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Show Columns
            </Typography>
            <Button
              size="small"
              onClick={() => {
                if (view === 'project') {
                  // Reset = the page's *default* visible set, i.e. every
                  // column minus the explicitly hidden-by-default ones
                  // (currently the "{N}BR fr." matched-from prices). Mirrors
                  // the agent's Master Tool reset semantics — never "select
                  // all".
                  setVisibleProjectCols(
                    new Set<ProjectColumnKey>(
                      PROJECT_COLUMNS
                        .map((c) => c.key as ProjectColumnKey)
                        .filter((key) => !PROJECT_DEFAULT_HIDDEN_COLUMNS.has(key)),
                    ),
                  );
                } else {
                  // Unit View has no hidden defaults today — reset still
                  // means "every column visible".
                  setVisibleUnitCols(new Set(UNIT_COLUMNS.map((c) => c.key)));
                }
              }}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <FormGroup>
            {view === 'project' ? (
              <>
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={
                        visibleProjectCols.size === PROJECT_DETAILS_ONLY_COLUMNS.length &&
                        PROJECT_DETAILS_ONLY_COLUMNS.every((k) => visibleProjectCols.has(k))
                      }
                      onChange={() => {
                        const presetSet = new Set<ProjectColumnKey>(PROJECT_DETAILS_ONLY_COLUMNS);
                        const isPresetActive =
                          visibleProjectCols.size === presetSet.size &&
                          Array.from(presetSet).every((k) => visibleProjectCols.has(k));
                        setVisibleProjectCols(
                          isPresetActive
                            ? new Set<ProjectColumnKey>(
                                PROJECT_COLUMNS
                                  .map((c) => c.key as ProjectColumnKey)
                                  .filter((key) => !PROJECT_DEFAULT_HIDDEN_COLUMNS.has(key)),
                              )
                            : presetSet,
                        );
                      }}
                    />
                  )}
                  label={(
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      PROJECT DETAILS ONLY
                    </Typography>
                  )}
                  sx={{ my: 0.25 }}
                />
                <Divider sx={{ my: 0.5 }} />
                {PROJECT_COLUMNS.map((c) => (
                  <FormControlLabel
                    key={c.key}
                    control={(
                      <Checkbox
                        size="small"
                        checked={visibleProjectCols.has(c.key)}
                        onChange={() =>
                          setVisibleProjectCols((prev) => {
                            const next = new Set(prev);
                            if (next.has(c.key)) next.delete(c.key);
                            else next.add(c.key);
                            return next;
                          })
                        }
                      />
                    )}
                    label={(
                      <Typography variant="body2">
                        {PROJECT_COLUMN_MENU_LABELS[c.key] ?? c.label}
                      </Typography>
                    )}
                    sx={{ my: 0.25 }}
                  />
                ))}
              </>
            ) : (
              UNIT_COLUMNS.map((c) => (
                <FormControlLabel
                  key={c.key}
                  control={(
                    <Checkbox
                      size="small"
                      checked={visibleUnitCols.has(c.key)}
                      onChange={() =>
                        setVisibleUnitCols((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.key)) next.delete(c.key);
                          else next.add(c.key);
                          return next;
                        })
                      }
                    />
                  )}
                  label={<Typography variant="body2">{c.label}</Typography>}
                  sx={{ my: 0.25 }}
                />
              ))
            )}
          </FormGroup>
        </Box>
      </Menu>
    </Box>
  );
};

export default ProjectResearchPage;
